import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, X, Phone, Mail, Tag, Wand2 } from "lucide-react";
import { api } from "../lib/api";

const STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "contacted", label: "Contacted" },
    { key: "qualified", label: "Qualified" },
    { key: "won", label: "Won" },
];

const STATUS_COLOR = {
    new: "bg-[#0066FF]/15 text-[#5C9BFF] border-[#0066FF]/30",
    contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    qualified: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    won: "bg-emerald-500/25 text-emerald-200 border-emerald-500/40",
    lost: "bg-red-500/15 text-red-300 border-red-500/30",
};

function ScorePill({ score = 0 }) {
    const color = score >= 75 ? "#10B981" : score >= 50 ? "#0066FF" : score >= 25 ? "#F59E0B" : "#EF4444";
    return (
        <div className="relative h-9 w-9 rounded-full grid place-items-center font-mono text-[11px]" style={{
            background: `conic-gradient(${color} ${score * 3.6}deg, rgba(255,255,255,0.05) 0)`,
        }}>
            <div className="absolute inset-[3px] rounded-full bg-[#0E0E11] grid place-items-center">
                <span style={{ color }}>{score}</span>
            </div>
        </div>
    );
}

function LeadCard({ lead, onOpen }) {
    return (
        <motion.button
            layout
            data-testid={`lead-card-${lead.id}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            onClick={() => onOpen(lead)}
            className="elate-card p-5 text-left hover:border-white/20 transition relative"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-medium">{lead.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{lead.campaign}</div>
                </div>
                <ScorePill score={lead.ai_score || 0} />
            </div>

            <div className="mt-4 flex items-center justify-between">
                <span className={`text-[10px] uppercase tracking-[0.15em] border rounded-full px-2 py-0.5 ${STATUS_COLOR[lead.status] || STATUS_COLOR.new}`}>
                    {lead.status}
                </span>
                <span className="text-[10px] text-zinc-500 font-mono">{lead.ad_platform || lead.source}</span>
            </div>
        </motion.button>
    );
}

function LeadDetail({ lead, onClose, onUpdated }) {
    const [scoring, setScoring] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [reply, setReply] = useState("");
    const [local, setLocal] = useState(lead);

    useEffect(() => setLocal(lead), [lead?.id]);

    const score = async () => {
        setScoring(true);
        try {
            const { data } = await api.post(`/leads/${lead.id}/score`);
            const updated = { ...local, ai_score: data.score, ai_summary: data.summary, ai_next_step: data.next_step };
            setLocal(updated);
            onUpdated(updated);
        } finally {
            setScoring(false);
        }
    };

    const draft = async () => {
        setDrafting(true);
        try {
            const { data } = await api.post(`/leads/${lead.id}/draft-reply`, { message: `Interested in ${lead.campaign}` });
            setReply(data.reply);
        } finally {
            setDrafting(false);
        }
    };

    const setStatus = async (s) => {
        const updated = { ...local, status: s };
        setLocal(updated);
        await api.patch(`/leads/${lead.id}`, { status: s });
        onUpdated(updated);
    };

    if (!lead) return null;
    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
                <motion.aside
                    initial={{ x: "100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "100%" }}
                    transition={{ duration: 0.3 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative ml-auto w-full max-w-xl bg-[#0A0A0C] border-l border-white/[0.08] h-full overflow-y-auto"
                    data-testid="lead-detail-panel"
                >
                    <div className="sticky top-0 backdrop-blur-xl bg-[#0A0A0C]/80 border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <ScorePill score={local.ai_score || 0} />
                            <div>
                                <div className="font-display text-base">{local.name}</div>
                                <div className="text-[11px] text-zinc-500 font-mono">{local.ad_platform} · {local.campaign}</div>
                            </div>
                        </div>
                        <button onClick={onClose} data-testid="lead-detail-close" className="p-2 rounded-lg hover:bg-white/[0.06]">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="elate-card p-4">
                                <div className="flex items-center gap-2 text-xs text-zinc-400"><Mail className="h-3.5 w-3.5" /> Email</div>
                                <div className="text-sm mt-1.5 break-all">{local.email || "—"}</div>
                            </div>
                            <div className="elate-card p-4">
                                <div className="flex items-center gap-2 text-xs text-zinc-400"><Phone className="h-3.5 w-3.5" /> Phone</div>
                                <div className="text-sm mt-1.5">{local.phone || "—"}</div>
                            </div>
                        </div>

                        {/* Status pills */}
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Status</div>
                            <div className="flex flex-wrap gap-2">
                                {["new", "contacted", "qualified", "won", "lost"].map((s) => (
                                    <button
                                        key={s}
                                        data-testid={`lead-status-${s}`}
                                        onClick={() => setStatus(s)}
                                        className={`text-xs px-3 py-1.5 rounded-full border capitalize transition ${
                                            local.status === s
                                                ? STATUS_COLOR[s]
                                                : "border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:text-white"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* AI score */}
                        <div className="elate-ai-border rounded-2xl bg-white/[0.02] p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-[#0066FF]" />
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Ana score</span>
                                </div>
                                <button
                                    data-testid="lead-score-button"
                                    onClick={score}
                                    disabled={scoring}
                                    className="text-xs rounded-full border border-white/10 px-3 py-1 hover:bg-white/[0.05] disabled:opacity-50"
                                >
                                    {scoring ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Re-score"}
                                </button>
                            </div>
                            {local.ai_summary ? (
                                <>
                                    <div className="text-sm text-zinc-200 leading-relaxed">{local.ai_summary}</div>
                                    {local.ai_next_step && (
                                        <div className="mt-3 text-xs text-zinc-400">
                                            <span className="text-[#5C9BFF]">Next step:</span> {local.ai_next_step}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-sm text-zinc-500">Click re-score to ask Ana for a fresh assessment.</div>
                            )}
                        </div>

                        {/* Draft reply */}
                        <div className="elate-card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Wand2 className="h-4 w-4 text-[#FF5500]" />
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Quick reply</span>
                                </div>
                                <button
                                    data-testid="lead-draft-reply"
                                    onClick={draft}
                                    disabled={drafting}
                                    className="text-xs rounded-full border border-white/10 px-3 py-1 hover:bg-white/[0.05] disabled:opacity-50"
                                >
                                    {drafting ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Draft with AI"}
                                </button>
                            </div>
                            <textarea
                                data-testid="lead-reply-text"
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                rows={4}
                                placeholder="AI-drafted reply will appear here…"
                                className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none resize-none"
                            />
                        </div>

                        {local.tags?.length > 0 && (
                            <div className="elate-card p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2"><Tag className="h-3.5 w-3.5" /> Tags</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {local.tags.map((t) => (
                                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-zinc-300">{t}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </motion.aside>
            </motion.div>
        </AnimatePresence>
    );
}

export default function Leads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("all");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        api.get("/leads").then(({ data }) => {
            setLeads(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const filtered = leads.filter((l) => tab === "all" || l.status === tab);

    return (
        <div className="space-y-6" data-testid="leads-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Lead engine</div>
                    <h2 className="font-display text-3xl mt-2">Leads</h2>
                </div>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                    {STATUS_TABS.map((t) => (
                        <button
                            key={t.key}
                            data-testid={`leads-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`px-3 py-1.5 rounded-full border transition ${
                                tab === t.key
                                    ? "border-white/20 bg-white/[0.06] text-white"
                                    : "border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="elate-card p-5 h-32 animate-pulse" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((l) => (
                        <LeadCard key={l.id} lead={l} onOpen={setSelected} />
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full text-sm text-zinc-500 py-16 text-center">No leads in this stage.</div>
                    )}
                </div>
            )}

            {selected && (
                <LeadDetail
                    lead={selected}
                    onClose={() => setSelected(null)}
                    onUpdated={(u) => {
                        setLeads((p) => p.map((l) => (l.id === u.id ? u : l)));
                        setSelected(u);
                    }}
                />
            )}
        </div>
    );
}
